import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

import {
  Textarea,
  TextareaProps,
} from '@dc-shared-web/components/shadcn/textarea'

export type AutoSizeTextAreaProps = Omit<TextareaProps, 'rows'> & {
  minRows?: number
  maxRows?: number
}

// In an ideal world we should set the textarea rows through react state and
// not directly on the element. However, we need to set the rows to 1 to accurately
// calculate the number of rows that matches the current content. Doing so with react will
// make it slower because of the re-rendering and the code would be more complex.
// Touching the DOM here directly is actually better, performance and complexity wise.
const setTextareaRows = (
  textarea: HTMLTextAreaElement,
  minRows: number,
  maxRows: number | undefined
) => {
  textarea.rows = 1
  textarea.style.overflow = 'hidden'

  const lineHeight =
    parseInt(
      window.getComputedStyle(textarea).getPropertyValue('line-height'),
      10
    ) || 20
  const scrollHeight = textarea.scrollHeight
  const rows = Math.max(Math.floor(scrollHeight / lineHeight), minRows)

  textarea.rows = Math.min(rows, maxRows ?? Infinity)
  textarea.style.overflow = ''
}

const AutoSizeTextArea = forwardRef<HTMLTextAreaElement, AutoSizeTextAreaProps>(
  (
    { minRows, maxRows, value, onChange, ...props }: AutoSizeTextAreaProps,
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e)
      setTextareaRows(e.target, minRows ?? 1, maxRows)
    }

    useImperativeHandle(ref, () => textareaRef.current!)

    useEffect(() => {
      if (!textareaRef.current) {
        return
      }

      if (value) {
        setTextareaRows(textareaRef.current, minRows ?? 1, maxRows)
      } else {
        textareaRef.current.rows = minRows ?? 1
      }
    }, [value, minRows, maxRows])

    return (
      <Textarea
        {...props}
        value={value}
        ref={textareaRef}
        onChange={handleChange}
      />
    )
  }
)

AutoSizeTextArea.displayName = 'AutoSizeTextArea'

export default AutoSizeTextArea
